import {action, computed, makeObservable, observable, runInAction} from 'mobx';

import {api} from './ApiFacade';
import {AppRoutes, logOverviewLink, projectDashboardLink} from './routing/AppRoutes';
import {showError, showWarning} from './helpers/alerts';
import {showLoaderAsync} from './helpers/loaders';
import {ProjectListDto, UserAuthApi, UserBasicInfo} from './swagger';
import {HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel} from '@microsoft/signalr';
import {DashboardState} from './views/dashboard/DashboardState';
import {LogOverviewViewState} from './views/log-overview/LogOverviewViewState';
import {isTestEnv} from './helpers/helpers';
import logo192 from './assets/images/logo192.png';
import Cookies from 'js-cookie';
import {demoEmail} from './views/login/LoginState';
import {redirectTo} from './routing/browserHistory';

const COOKIE_NOTIFICATIONS_ENABLED = 'notifications';

export const NO_AUTOLOGIN_QUERY_PARAM = 'noautologin';

class AppMainState {
    @observable
    isShrinked: boolean = false;
    @observable
    currentUser?: UserBasicInfo;
    @observable
    projects: Array<ProjectListDto> = [];
    @observable
    isLoading: boolean = false;
    @observable
    appInitialized: boolean = false;
    @observable
    browserNotifications: boolean = false;
    signalRstarted = false;
    dashboardState?: DashboardState;
    logOverviewState?: LogOverviewViewState;
    signalRHubConnection?: HubConnection;
    @observable
    isTourOpen: boolean = false;
    redirectAfterLogin?: string;

    constructor() {
        makeObservable(this);
        this.loadBaseAppInformation();
        if (parseInt(Cookies.get(COOKIE_NOTIFICATIONS_ENABLED) as any)) {
            this.handleBrowserNotificationPermission();
        }
    }

    loadBaseAppInformation() {
        api()
            .projects.apiProjectsBaseAppInformationGet()
            .then((res) => {
                runInAction(() => {
                    this.projects = res.content?.projectList ?? [];
                    this.currentUser = res.content?.userBasicInfo;

                    try {
                        this.initSignalR();
                    } catch (e) {
                        console.error('Error when initializing SignalR', e);
                    }

                    //showChangeInfo(this.currentUser);

                    if (this.currentUser?.acceptedTermsOfUse) {
                        if (this.redirectAfterLogin) {
                            redirectTo(this.redirectAfterLogin);
                            this.redirectAfterLogin = undefined;
                        } else {
                            this.redirectToDashboard();
                        }
                    } else {
                        redirectTo(AppRoutes.TermsOfUse);
                    }
                });
            })
            .catch((err) => {
                runInAction(() => {
                    this.currentUser = undefined;
                });

                console.debug('User not logged in');
                if (err.status && err.status === 500) {
                    showError('Fatal error when checking status', 'Please try again later', err);
                }
            })
            .finally(() => {
                if (!this.appInitialized) {
                    runInAction(() => {
                        this.appInitialized = true;
                    });
                    document.getElementById('splash')!.remove();
                }
            });
    }

    initSignalR() {
        if (this.projects.length === 0) {
            return;
        }

        this.signalRstarted = true;

        this.signalRHubConnection = new HubConnectionBuilder()
            .withUrl(this.currentUser?.notificationsUrl + '/notifications')
            .withAutomaticReconnect()
            .configureLogging(isTestEnv() ? LogLevel.Information : LogLevel.Error)
            .build();

        this.signalRHubConnection.start()
            .then(() => console.debug('SignalR initialized'))
            .catch(err => console.error('Error when starting SignalR signalRHubConnection', err));

        this.signalRHubConnection.on('Notification', async (notificationString: string) => {
            const notification = JSON.parse(notificationString);
            if (!notification?.LogId) {
                return;
            }
            const logId = notification.LogId;
            try {
                new Notification('Muscula', {body: notification.Severity + ': ' + notification.Message, icon: logo192});
            } catch (e) {
                console.error('Error when sending notification', e);
            }

            console.debug('Notification for Log ' + logId);
            if (this.dashboardState) {
                const projectId = this.projects.find(x => x.logs?.find(l => l.logId === logId) !== undefined)?.projectId;
                if (this.dashboardState.projectId === projectId) {
                    await this.dashboardState.refreshDashboard();
                }
            }

            if (this.logOverviewState && this.logOverviewState.logId === logId) {
                await this.logOverviewState.refreshFlats();
            }
        });


        document.addEventListener('visibilitychange', async () => {
            if (this.signalRHubConnection !== undefined && document.hidden && this.signalRHubConnection.state === HubConnectionState.Connected && !this.browserNotifications) {
                setTimeout(() => {
                    if (this.signalRHubConnection !== undefined && document.hidden && this.signalRHubConnection.state === HubConnectionState.Connected) {
                        this.signalRHubConnection.stop();
                    }
                }, 10000);
            }


            if (!document.hidden && this.signalRHubConnection !== undefined && this.signalRHubConnection.state === HubConnectionState.Disconnected) {
                if (this.dashboardState) {
                    await this.dashboardState.refreshDashboard();
                }

                if (this.logOverviewState) {
                    await this.logOverviewState.refreshFlats();
                }

                this.signalRHubConnection.start().catch(err => console.error('Error when reactivating signalRHubConnection', err));
            }
        });
    }

    @computed
    get isDemoUser(): boolean {
        return this.currentUser?.email === demoEmail;
    }

    redirectToDashboard() {
        if (
            this.projects &&
            this.projects.length > 0 &&
            (document.location.pathname === '/' || document.location.pathname === AppRoutes.TermsOfUse)
        ) {
            if (this.isDemoUser) {
                if (this.isShrinked) {
                    // @ts-ignore
                    redirectTo(logOverviewLink(this.projects[0].logs[0].logId));
                } else {
                    // @ts-ignore
                    redirectTo(AppRoutes.Projects);
                }

            } else {
                redirectTo(
                    projectDashboardLink(this.projects[0].projectId!)
                );
            }

        }

        if (!this.projects || this.projects.length === 0) {
            redirectTo(AppRoutes.CreateProject);
        }
    }

    reloadProjects() {
        api()
            .projects.apiProjectsBaseAppInformationGet()
            .then((res) => {
                runInAction(() => {
                    this.projects = res.content?.projectList ?? [];
                    this.currentUser = res.content?.userBasicInfo;

                    if (!this.signalRstarted) {
                        this.initSignalR();
                    }
                });
            })
            .catch((err) => {
                showError(
                    'Error when loading projects',
                    'Please try again later',
                    err
                );
            });
    }

    @action
    signOut() {
        if (this.currentUser?.social) {
            //TODO: Dodać poprawne wylogowanie z Google
        }

        if (this.signalRHubConnection !== undefined) {
            this.signalRHubConnection.stop();
        }

        showLoaderAsync(
            new UserAuthApi()
                .apiUserAuthSignOutDelete()
                .then(() => {
                    runInAction(() => {
                        this.currentUser = undefined;
                    });
                })
                .catch((res) => {
                    runInAction(() => {
                        this.currentUser = undefined;
                    });
                    console.error('Error when signing out', res);
                }).finally(() => {
                redirectTo(AppRoutes.Login + '?' + NO_AUTOLOGIN_QUERY_PARAM + '=1');
                location.reload();
            })
        );
    }

    @action
    decrementStarred() {
        this.currentUser!.starred = (this.currentUser!.starred ?? 1) - 1;
    }

    @action
    incrementStarred() {
        this.currentUser!.starred = (this.currentUser?.starred ?? 0) + 1;
    }

    @action
    decrementAssignedTo() {
        this.currentUser!.assigned = (this.currentUser!.assigned ?? 1) - 1;
    }

    @action
    incrementAssignedTo() {
        this.currentUser!.assigned = (this.currentUser?.assigned ?? 0) + 1;
    }

    @computed
    get isActiveSubscription(): boolean {
        return !mainState.currentUser?.accountExpired ?? false;
    }

    reportProblem() {
        try {
            // @ts-ignore
            Ybug.open();
        } catch (err) {
            showError('Cannot open widget to report a problem', 'Please disable tracking control extensions, some can block them', err);
        }

    }


    handleBrowserNotificationPermission() {
        runInAction(() => {
            this.browserNotifications = !(Notification.permission==='denied' || Notification.permission==='default');
        });
    }

    checkNotificationPromise(): boolean {
        try {
            Notification.requestPermission().then();
        } catch (e) {
            return false;
        }

        return true;
    }

    @action
    toggleBrowserNotifications() {
        if (!this.browserNotifications) {
            Cookies.set(COOKIE_NOTIFICATIONS_ENABLED, '1');
            if (!('Notification' in window)) {
                showWarning('Error', 'This browser does not support notifications.');
            } else {
                if (this.checkNotificationPromise()) {
                    Notification.requestPermission()
                        .then(() => {
                            this.handleBrowserNotificationPermission();
                        });
                } else {
                    Notification.requestPermission(() => {
                        this.handleBrowserNotificationPermission();
                    });
                }
            }
        } else {
            Cookies.set(COOKIE_NOTIFICATIONS_ENABLED, '0');
            this.browserNotifications = false;
        }

    }

    @action
    setIsTourOpen(v: boolean) {
        this.isTourOpen = v;
    }
}

export const mainState = new AppMainState();
